/**
 * Tokenomics Public Report Page — Vertical 5 (Tokenomics-as-a-Service B2B).
 *
 * Public, no-auth read-only report linked from a token project's own site.
 * Investor-facing presentation. Honest disclosures throughout.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, AlertCircle, Calendar, Users, ShieldCheck } from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import { getTokenomicsPublicReport } from '../services/api';

function StatBox({ label, value, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">{value ?? <span className="text-slate-400 text-base">—</span>}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function PendingNotice({ reason }) {
  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-medium">Live data not available</div>
        <div className="text-amber-700 mt-0.5">{reason || 'Holder distribution could not be fetched at this time.'}</div>
      </div>
    </div>
  );
}

export default function TokenomicsPublicReportPage() {
  const { publicSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getTokenomicsPublicReport(publicSlug);
        if (cancelled) return;
        if (!r?.success) {
          setError(r?.error?.message || 'Report not available');
          setLoading(false);
          return;
        }
        setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading report…</div>;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center shadow-sm">
          <div className="text-rose-500 mb-2"><AlertCircle className="w-8 h-8 mx-auto" /></div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Report not available</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <Link to="/" className="text-sm text-emerald-600 hover:underline">← Back to haythix</Link>
        </div>
      </div>
    );
  }

  const r = data.report;
  const holders = data.holderAnalysis;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="haythix" className="w-7 h-7" />
            <span className="font-semibold text-slate-800">haythix</span>
          </Link>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Tokenomics Report · v{data.reportVersion}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Project</div>
          <h1 className="text-3xl font-bold text-slate-900">{r.name}</h1>
          <div className="text-sm text-slate-500 mt-1">
            Published {new Date(r.updatedAt).toLocaleDateString()} · Generated {new Date(data.generatedAt).toLocaleString()}
          </div>
        </div>

        {/* Token list */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Tokens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {r.tokens.length === 0 && <div className="text-sm text-slate-500">No tokens registered.</div>}
            {r.tokens.map(t => (
              <div key={`${t.network}-${t.contractAddress}`} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-slate-900">{t.symbol}</div>
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{t.network}</span>
                </div>
                <div className="text-sm text-slate-500">{t.name}</div>
                <a href={`https://etherscan.io/address/${t.contractAddress}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-emerald-700 hover:underline font-mono break-all flex items-center gap-1 mt-2">
                  {t.contractAddress} <ExternalLink className="w-3 h-3" />
                </a>
                {t.primaryPair?.address && (
                  <div className="text-xs text-slate-500 mt-1">Primary pair vs {t.primaryPair.quoteSymbol}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Holder distribution (real, live) */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Holder distribution
          </h2>
          {!holders && <div className="text-sm text-slate-500">Holder analysis not run yet.</div>}
          {holders?.status === 'pending' && <PendingNotice reason={holders.reason} />}
          {holders?.status === 'ok' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatBox label="Top 10 hold" value={holders.metrics.top10ConcentrationPct !== null ? `${holders.metrics.top10ConcentrationPct}%` : null} />
                <StatBox label="Top 50 hold" value={holders.metrics.top50ConcentrationPct !== null ? `${holders.metrics.top50ConcentrationPct}%` : null} />
                <StatBox label="Gini coefficient" value={holders.metrics.giniCoefficient} hint="0 = even, 1 = concentrated" />
                <StatBox label="CEX wallets in top-N" value={holders.metrics.cexHolderCount} />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">Top 25 holders</div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Address</th>
                      <th className="text-left px-3 py-2">Label</th>
                      <th className="text-right px-3 py-2">% of supply</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {holders.top.slice(0, 25).map((h, i) => (
                      <tr key={h.address}>
                        <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-1.5 text-slate-700 font-mono text-xs">{h.address.slice(0, 10)}…{h.address.slice(-6)}</td>
                        <td className="px-3 py-1.5 text-slate-600">{h.label || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-700 text-right">{h.percentage !== null ? `${h.percentage}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-slate-500 mt-2 space-y-1">
                <div>{holders.disclosures?.decimalsCaveat}</div>
                <div>{holders.disclosures?.sampleCaveat}</div>
              </div>
            </>
          )}
        </section>

        {/* Unlock calendar */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Unlock calendar
          </h2>
          {r.unlocks.length === 0 ? (
            <div className="text-sm text-slate-500">No unlock events disclosed.</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Token</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">% of supply</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {r.unlocks.map(u => (
                    <tr key={u.unlockId}>
                      <td className="px-3 py-2 text-slate-700">{u.eventDate}</td>
                      <td className="px-3 py-2 text-slate-700 font-medium">{u.tokenSymbol}</td>
                      <td className="px-3 py-2 text-slate-700 text-right">{Number(u.amount).toLocaleString()}</td>
                      <td className="px-3 py-2 text-slate-700 text-right">{u.percentOfSupply !== null && u.percentOfSupply !== undefined ? `${u.percentOfSupply}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Disclosures */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-600 space-y-2">
          <h3 className="text-base font-semibold text-slate-900">Disclosures</h3>
          <p>{data.disclosures.notEndorsement}</p>
          <p>{data.disclosures.dataFreshness}</p>
          <p className="font-medium">{data.disclosures.notFinancialAdvice}</p>
        </section>

        <footer className="text-center text-xs text-slate-500 pt-4">
          Hosted by <Link to="/" className="text-emerald-700 hover:underline">haythix</Link> · Report v{data.reportVersion} · {new Date(data.generatedAt).toLocaleString()}
        </footer>
      </main>
    </div>
  );
}
